import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  User,
  PersonalUserSettings,
  CarPhotoAngle,
  CarInfo,
  CarSettings,
  CarSettingsCreateUpdateRequest,
  SelfReference,
  FamilyReference,
  GarageReference,
  StaffReference,
  CarModelReference,
  CarSpotReference,
  OtherReference,
  AllReferencesResponse,
  SelfPhotoType,
  ReferenceCategory,
  StaffStyle,
  CarModelStyle,
  SpotCategory,
  SelfPhotoTypeOption,
  ReferenceCategoryOption,
  StaffStyleOption,
  CarModelStyleOption,
  SpotCategoryOption,
} from '../types';
import { ImageUpload, ImageUploadRef } from '@/components/ImageUpload';
import {
  XMarkIcon,
  CheckCircleIcon as SaveIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  TruckIcon,
  UsersIcon,
  StarIcon,
  UserGroupIcon,
  HomeModernIcon,
  SparklesSolidIcon,
} from '../components/icons/HeroIcons';
import { getCarInfo } from '../services/api/car-info';
import {
  fetchCarSettings,
  createOrUpdateCarSettings,
  updateCarSettings,
  deleteCarSettings,
} from '../services/api/car-settings';
import {
  fetchAllReferences,
  createOrUpdateSelfReference,
  deleteSelfReference,
  createFamilyReference,
  updateFamilyReference,
  deleteFamilyReference,
  createGarageReference,
  updateGarageReference,
  deleteGarageReference,
  createStaffReference,
  updateStaffReference,
  deleteStaffReference,
  createCarModelReference,
  updateCarModelReference,
  deleteCarModelReference,
  createCarSpotReference,
  updateCarSpotReference,
  deleteCarSpotReference,
  createOtherReference,
  updateOtherReference,
  deleteOtherReference,
} from '../services/api/user-references';
import { useToast } from '../contexts/ToastContext';

// 自分リファレンス用のラベル
const selfPhotoTypeLabels: Record<SelfPhotoType, string> = {
  front: '正面写真',
  front_smile: '正面笑顔写真',
  diagonal: '斜め顔写真',
};

// その他リファレンス用のカテゴリラベル
const referenceCategoryLabels: Record<ReferenceCategory, string> = {
  person: '人物',
  car: '車',
  animal: '動物',
  object: 'オブジェクト',
  background: '背景',
  costume: '衣装',
};

// スタッフリファレンス用のスタイルラベル
const staffStyleLabels: Record<StaffStyle, string> = {
  real: 'リアル',
  anime: 'アニメ',
  illustration: 'イラスト',
  '3d': '3D',
};

// カーモデルリファレンス用のスタイルラベル
const carModelStyleLabels: Record<CarModelStyle, string> = {
  real: 'リアル',
  anime: 'アニメ',
  illustration: 'イラスト',
  '3d': '3D',
};

// カースポットリファレンス用のカテゴリラベル
const spotCategoryLabels: Record<SpotCategory, string> = {
  scenic: '景勝地',
  urban: '都市',
  mountain: '山',
  seaside: '海辺',
  circuit: 'サーキット',
  parking: '駐車場',
  other: 'その他',
};

// 愛車写真用のラベル
const carPhotoAngleLabels: Record<CarPhotoAngle, string> = {
  front: 'フロント正面',
  side: '真横',
  rear: 'リア正面',
  front_angled_7_3: '斜め前 7:3',
  rear_angled_7_3: '斜め後 7:3',
};

export const getDefaultPersonalUserSettings = (): PersonalUserSettings => ({
  numberManagement: {
    licensePlateText: '',
    logoMarkImageUrl: undefined,
    originalNumberImageUrl: undefined,
  },
  referenceRegistration: {
    favoriteCarName: '',
    carPhotos: (Object.keys(carPhotoAngleLabels) as CarPhotoAngle[]).map(
      (angle) => ({
        viewAngle: angle,
        label: carPhotoAngleLabels[angle],
        imageUrl: undefined,
      }),
    ),
  },
});

interface ReferenceSettingsViewProps {
  currentUser: User;
  onSave: (settings: PersonalUserSettings) => void;
  onClose: () => void;
}

const ReferenceSettingsView: React.FC<ReferenceSettingsViewProps> = ({
  currentUser,
  onSave,
  onClose,
}) => {
  const { showToast } = useToast();

  // セグメント開閉状態
  const [isSelfReferenceOpen, setIsSelfReferenceOpen] = useState(true);
  const [isCarReferenceOpen, setIsCarReferenceOpen] = useState(false);
  const [isFamilyReferenceOpen, setIsFamilyReferenceOpen] = useState(false);
  const [isOtherReferenceOpen, setIsOtherReferenceOpen] = useState(false);

  // 自分リファレンス
  const [selfReferences, setSelfReferences] = useState<SelfReference[]>([]);
  const [selfPhotoTypes, setSelfPhotoTypes] = useState<SelfPhotoTypeOption[]>([]);

  // ファミリーリファレンス
  const [familyReferences, setFamilyReferences] = useState<FamilyReference[]>([]);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newFamilyPhoto, setNewFamilyPhoto] = useState<File | null>(null);

  // ガレージリファレンス
  const [garageReferences, setGarageReferences] = useState<GarageReference[]>([]);
  const [newGarageName, setNewGarageName] = useState('');
  const [newGaragePhoto, setNewGaragePhoto] = useState<File | null>(null);

  // スタッフリファレンス
  const [staffReferences, setStaffReferences] = useState<StaffReference[]>([]);
  const [staffStyles, setStaffStyles] = useState<StaffStyleOption[]>([]);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffStyle, setNewStaffStyle] = useState<StaffStyle>('real');
  const [newStaffPhoto, setNewStaffPhoto] = useState<File | null>(null);
  const [newStaffDisplayImage, setNewStaffDisplayImage] = useState<File | null>(null);

  // カーモデルリファレンス
  const [carModelReferences, setCarModelReferences] = useState<CarModelReference[]>([]);
  const [carModelStyles, setCarModelStyles] = useState<CarModelStyleOption[]>([]);
  const [newCarModelName, setNewCarModelName] = useState('');
  const [newCarModelStyle, setNewCarModelStyle] = useState<CarModelStyle>('real');
  const [newCarModelPhoto, setNewCarModelPhoto] = useState<File | null>(null);
  const [newCarModelDisplayImage, setNewCarModelDisplayImage] = useState<File | null>(null);

  // カースポットリファレンス
  const [carSpotReferences, setCarSpotReferences] = useState<CarSpotReference[]>([]);
  const [spotCategories, setSpotCategories] = useState<SpotCategoryOption[]>([]);
  const [newCarSpotName, setNewCarSpotName] = useState('');
  const [newCarSpotCategory, setNewCarSpotCategory] = useState<SpotCategory>('other');
  const [newCarSpotPhoto, setNewCarSpotPhoto] = useState<File | null>(null);
  const [newCarSpotDisplayImage, setNewCarSpotDisplayImage] = useState<File | null>(null);

  // その他リファレンス
  const [otherReferences, setOtherReferences] = useState<OtherReference[]>([]);
  const [referenceCategories, setReferenceCategories] = useState<ReferenceCategoryOption[]>([]);
  const [newOtherName, setNewOtherName] = useState('');
  const [newOtherCategory, setNewOtherCategory] = useState<ReferenceCategory>('person');
  const [newOtherPhoto, setNewOtherPhoto] = useState<File | null>(null);

  // 愛車リファレンス（既存機能）
  const [settings, setSettings] = useState<PersonalUserSettings>(
    currentUser.personalSettings || getDefaultPersonalUserSettings(),
  );
  const [carList, setCarList] = useState<CarInfo[]>([]);
  const [selectedCar, setSelectedCar] = useState<CarInfo | null>(null);
  const [isCarSelectModalOpen, setIsCarSelectModalOpen] = useState(false);
  const [currentCarSettings, setCurrentCarSettings] = useState<CarSettings | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<{
    original_number_image?: File;
    car_photo_front?: File;
    car_photo_side?: File;
    car_photo_rear?: File;
    car_photo_diagonal?: File;
    car_photo_rear_diagonal?: File;
  }>({});
  const [deleteFlags, setDeleteFlags] = useState<{
    delete_original_number_image?: boolean;
    delete_car_photo_front?: boolean;
    delete_car_photo_side?: boolean;
    delete_car_photo_rear?: boolean;
    delete_car_photo_diagonal?: boolean;
    delete_car_photo_rear_diagonal?: boolean;
  }>({});

  // DEBUG: uploadedFilesの変更を監視
  useEffect(() => {
    const keys = Object.keys(uploadedFiles);
    console.log(`[DEBUG] uploadedFiles changed: keys=[${keys.join(', ')}], count=${keys.length}`);
  }, [uploadedFiles]);

  // ローディング状態
  const [isLoading, setIsLoading] = useState(false);

  // ImageUploadのRef
  const originalNumberImageUploadRef = useRef<ImageUploadRef>(null);

  // 初期データ読み込み
  useEffect(() => {
    const loadInitialData = async () => {
      if (!currentUser.id) return;
      setIsLoading(true);
      try {
        // 全参照設定を取得
        const allRefs = await fetchAllReferences(currentUser.id);
        if (allRefs) {
          setSelfReferences(allRefs.self_references);
          setFamilyReferences(allRefs.family_references);
          setGarageReferences(allRefs.garage_references || []);
          setStaffReferences(allRefs.staff_references || []);
          setCarModelReferences(allRefs.car_model_references || []);
          setCarSpotReferences(allRefs.car_spot_references || []);
          setOtherReferences(allRefs.other_references);
          setSelfPhotoTypes(allRefs.self_photo_types);
          setReferenceCategories(allRefs.reference_categories);
          setStaffStyles(allRefs.staff_styles || []);
          setCarModelStyles(allRefs.car_model_styles || []);
          setSpotCategories(allRefs.spot_categories || []);
        }

        // 愛車情報を取得
        const cars = await getCarInfo(currentUser.id);
        setCarList(cars);
        if (cars.length > 0) {
          setSelectedCar(cars[0]);
          // 選択した愛車の設定を取得
          const carSettingsList = await fetchCarSettings(currentUser.id);
          if (carSettingsList && carSettingsList.length > 0) {
            const carSetting = carSettingsList.find(cs => cs.car_id === cars[0].car_id);
            if (carSetting) {
              setCurrentCarSettings(carSetting);
              setSettings(prev => ({
                ...prev,
                numberManagement: {
                  licensePlateText: carSetting.license_plate_text || '',
                  logoMarkImageUrl: carSetting.logo_mark_image_url || undefined,
                  originalNumberImageUrl: carSetting.original_number_image_url || undefined,
                },
                referenceRegistration: {
                  favoriteCarName: carSetting.car_name || '',
                  carPhotos: prev.referenceRegistration.carPhotos.map(cp => {
                    if (cp.viewAngle === 'front') return { ...cp, imageUrl: carSetting.car_photo_front_url || undefined };
                    if (cp.viewAngle === 'side') return { ...cp, imageUrl: carSetting.car_photo_side_url || undefined };
                    if (cp.viewAngle === 'rear') return { ...cp, imageUrl: carSetting.car_photo_rear_url || undefined };
                    if (cp.viewAngle === 'front_angled_7_3') return { ...cp, imageUrl: carSetting.car_photo_diagonal_url || undefined };
                    if (cp.viewAngle === 'rear_angled_7_3') return { ...cp, imageUrl: carSetting.car_photo_rear_diagonal_url || undefined };
                    return cp;
                  }),
                },
              }));
            }
          }
        }
      } catch (error) {
        console.error('初期データ読み込みエラー:', error);
        showToast('error', 'データの読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [currentUser.id]);

  // 自分リファレンスの写真アップロード
  const handleSelfPhotoUpload = async (photoType: SelfPhotoType, file: File | null) => {
    if (!file || !currentUser.id) return;
    setIsLoading(true);
    try {
      const result = await createOrUpdateSelfReference(currentUser.id, photoType, file);
      if (result) {
        setSelfReferences(prev => {
          const existing = prev.find(r => r.photo_type === photoType);
          if (existing) {
            return prev.map(r => r.photo_type === photoType ? result : r);
          } else {
            return [...prev, result];
          }
        });
        showToast('success', '写真を保存しました');
      }
    } catch (error) {
      console.error('自分リファレンス保存エラー:', error);
      showToast('error', '写真の保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 自分リファレンスの削除
  const handleDeleteSelfReference = async (id: number) => {
    if (!confirm('この写真を削除しますか？')) return;
    setIsLoading(true);
    try {
      const success = await deleteSelfReference(id);
      if (success) {
        setSelfReferences(prev => prev.filter(r => r.id !== id));
        showToast('success', '写真を削除しました');
      }
    } catch (error) {
      console.error('自分リファレンス削除エラー:', error);
      showToast('error', '削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ファミリーリファレンスの追加
  const handleAddFamilyReference = async () => {
    if (!newFamilyName.trim() || !currentUser.id) {
      showToast('error', 'ファミリー名を入力してください');
      return;
    }
    if (familyReferences.length >= 5) {
      showToast('error', 'ファミリーリファレンスは最大5件までです');
      return;
    }
    setIsLoading(true);
    try {
      const result = await createFamilyReference(
        currentUser.id,
        newFamilyName.trim(),
        newFamilyPhoto || undefined,
      );
      if (result) {
        setFamilyReferences(prev => [...prev, result]);
        setNewFamilyName('');
        setNewFamilyPhoto(null);
        showToast('success', 'ファミリーリファレンスを追加しました');
      }
    } catch (error) {
      console.error('ファミリーリファレンス追加エラー:', error);
      showToast('error', '追加に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ファミリーリファレンスの写真更新
  const handleUpdateFamilyPhoto = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateFamilyReference(id, { photo: file });
      if (result) {
        setFamilyReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '写真を更新しました');
      }
    } catch (error) {
      console.error('ファミリーリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ファミリーリファレンスの名前更新
  const handleUpdateFamilyName = async (id: number, newName: string) => {
    if (!newName.trim()) {
      showToast('error', 'ファミリー名を入力してください');
      return;
    }
    setIsLoading(true);
    try {
      const result = await updateFamilyReference(id, { family_name: newName.trim() });
      if (result) {
        setFamilyReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'ファミリー名を更新しました');
      }
    } catch (error) {
      console.error('ファミリーリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ファミリーリファレンスの削除
  const handleDeleteFamilyReference = async (id: number) => {
    if (!confirm('このファミリーリファレンスを削除しますか？')) return;
    setIsLoading(true);
    try {
      const success = await deleteFamilyReference(id);
      if (success) {
        setFamilyReferences(prev => prev.filter(r => r.id !== id));
        showToast('success', 'ファミリーリファレンスを削除しました');
      }
    } catch (error) {
      console.error('ファミリーリファレンス削除エラー:', error);
      showToast('error', '削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ガレージリファレンスの追加
  const handleAddGarageReference = async () => {
    if (!newGarageName.trim() || !currentUser.id) {
      showToast('error', 'ガレージ名を入力してください');
      return;
    }
    if (garageReferences.length >= 5) {
      showToast('error', 'ガレージリファレンスは最大5件までです');
      return;
    }
    setIsLoading(true);
    try {
      const result = await createGarageReference(
        currentUser.id,
        newGarageName.trim(),
        newGaragePhoto || undefined,
      );
      if (result) {
        setGarageReferences(prev => [...prev, result]);
        setNewGarageName('');
        setNewGaragePhoto(null);
        showToast('success', 'ガレージリファレンスを追加しました');
      }
    } catch (error) {
      console.error('ガレージリファレンス追加エラー:', error);
      showToast('error', '追加に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ガレージリファレンスの写真更新
  const handleUpdateGaragePhoto = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateGarageReference(id, { photo: file });
      if (result) {
        setGarageReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '写真を更新しました');
      }
    } catch (error) {
      console.error('ガレージリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ガレージリファレンスの名前更新
  const handleUpdateGarageName = async (id: number, newName: string) => {
    if (!newName.trim()) {
      showToast('error', 'ガレージ名を入力してください');
      return;
    }
    setIsLoading(true);
    try {
      const result = await updateGarageReference(id, { garageName: newName.trim() });
      if (result) {
        setGarageReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'ガレージ名を更新しました');
      }
    } catch (error) {
      console.error('ガレージリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ガレージリファレンスの削除
  const handleDeleteGarageReference = async (id: number) => {
    if (!confirm('このガレージリファレンスを削除しますか？')) return;
    setIsLoading(true);
    try {
      const success = await deleteGarageReference(id);
      if (success) {
        setGarageReferences(prev => prev.filter(r => r.id !== id));
        showToast('success', 'ガレージリファレンスを削除しました');
      }
    } catch (error) {
      console.error('ガレージリファレンス削除エラー:', error);
      showToast('error', '削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // その他リファレンスの追加
  const handleAddOtherReference = async () => {
    if (!newOtherName.trim() || !currentUser.id) {
      showToast('error', 'リファレンス名を入力してください');
      return;
    }
    if (otherReferences.length >= 5) {
      showToast('error', 'お気に入りリファレンスは最大5件までです');
      return;
    }
    setIsLoading(true);
    try {
      const result = await createOtherReference(
        currentUser.id,
        newOtherName.trim(),
        newOtherCategory,
        newOtherPhoto || undefined,
      );
      if (result) {
        setOtherReferences(prev => [...prev, result]);
        setNewOtherName('');
        setNewOtherCategory('person');
        setNewOtherPhoto(null);
        showToast('success', 'お気に入りリファレンスを追加しました');
      }
    } catch (error) {
      console.error('その他リファレンス追加エラー:', error);
      showToast('error', '追加に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // その他リファレンスの写真更新
  const handleUpdateOtherPhoto = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateOtherReference(id, { photo: file });
      if (result) {
        setOtherReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '写真を更新しました');
      }
    } catch (error) {
      console.error('その他リファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // その他リファレンスの名前更新
  const handleUpdateOtherName = async (id: number, newName: string) => {
    if (!newName.trim()) {
      showToast('error', 'リファレンス名を入力してください');
      return;
    }
    setIsLoading(true);
    try {
      const result = await updateOtherReference(id, { reference_name: newName.trim() });
      if (result) {
        setOtherReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'リファレンス名を更新しました');
      }
    } catch (error) {
      console.error('その他リファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // その他リファレンスの削除
  const handleDeleteOtherReference = async (id: number) => {
    if (!confirm('このお気に入りリファレンスを削除しますか？')) return;
    setIsLoading(true);
    try {
      const success = await deleteOtherReference(id);
      if (success) {
        setOtherReferences(prev => prev.filter(r => r.id !== id));
        showToast('success', 'お気に入りリファレンスを削除しました');
      }
    } catch (error) {
      console.error('その他リファレンス削除エラー:', error);
      showToast('error', '削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // スタッフリファレンスの追加
  const handleAddStaffReference = async () => {
    if (!newStaffName.trim() || !currentUser.id) {
      showToast('error', 'タレント名を入力してください');
      return;
    }
    if (staffReferences.length >= 5) {
      showToast('error', 'スタッフリファレンスは最大5件までです');
      return;
    }
    setIsLoading(true);
    try {
      const result = await createStaffReference(
        currentUser.id,
        newStaffName.trim(),
        newStaffStyle,
        newStaffPhoto || undefined,
        newStaffDisplayImage || undefined,
      );
      if (result) {
        setStaffReferences(prev => [...prev, result]);
        setNewStaffName('');
        setNewStaffStyle('real');
        setNewStaffPhoto(null);
        setNewStaffDisplayImage(null);
        showToast('success', 'スタッフリファレンスを追加しました');
      }
    } catch (error) {
      console.error('スタッフリファレンス追加エラー:', error);
      showToast('error', '追加に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // スタッフリファレンスの参照画像更新
  const handleUpdateStaffPhoto = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateStaffReference(id, { photo: file });
      if (result) {
        setStaffReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '参照画像を更新しました');
      }
    } catch (error) {
      console.error('スタッフリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // スタッフリファレンスの表示画像更新
  const handleUpdateStaffDisplayImage = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateStaffReference(id, { display_image: file });
      if (result) {
        setStaffReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '表示画像を更新しました');
      }
    } catch (error) {
      console.error('スタッフリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // スタッフリファレンスの名前更新
  const handleUpdateStaffName = async (id: number, newName: string) => {
    if (!newName.trim()) {
      showToast('error', 'タレント名を入力してください');
      return;
    }
    setIsLoading(true);
    try {
      const result = await updateStaffReference(id, { staff_name: newName.trim() });
      if (result) {
        setStaffReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'タレント名を更新しました');
      }
    } catch (error) {
      console.error('スタッフリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // スタッフリファレンスのスタイル更新
  const handleUpdateStaffStyle = async (id: number, newStyle: StaffStyle) => {
    setIsLoading(true);
    try {
      const result = await updateStaffReference(id, { style: newStyle });
      if (result) {
        setStaffReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'スタイルを更新しました');
      }
    } catch (error) {
      console.error('スタッフリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // スタッフリファレンスの削除
  const handleDeleteStaffReference = async (id: number) => {
    if (!confirm('このスタッフリファレンスを削除しますか？')) return;
    setIsLoading(true);
    try {
      const success = await deleteStaffReference(id);
      if (success) {
        setStaffReferences(prev => prev.filter(r => r.id !== id));
        showToast('success', 'スタッフリファレンスを削除しました');
      }
    } catch (error) {
      console.error('スタッフリファレンス削除エラー:', error);
      showToast('error', '削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カーモデルリファレンスの追加
  const handleAddCarModelReference = async () => {
    if (!newCarModelName.trim() || !currentUser.id) {
      showToast('error', 'モデル名を入力してください');
      return;
    }
    if (carModelReferences.length >= 5) {
      showToast('error', 'カーモデルリファレンスは最大5件までです');
      return;
    }
    setIsLoading(true);
    try {
      const result = await createCarModelReference(
        currentUser.id,
        newCarModelName.trim(),
        newCarModelStyle,
        newCarModelPhoto || undefined,
        newCarModelDisplayImage || undefined,
      );
      if (result) {
        setCarModelReferences(prev => [...prev, result]);
        setNewCarModelName('');
        setNewCarModelStyle('real');
        setNewCarModelPhoto(null);
        setNewCarModelDisplayImage(null);
        showToast('success', 'カーモデルリファレンスを追加しました');
      }
    } catch (error) {
      console.error('カーモデルリファレンス追加エラー:', error);
      showToast('error', '追加に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カーモデルリファレンスの参照画像更新
  const handleUpdateCarModelPhoto = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateCarModelReference(id, { photo: file });
      if (result) {
        setCarModelReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '参照画像を更新しました');
      }
    } catch (error) {
      console.error('カーモデルリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カーモデルリファレンスの表示画像更新
  const handleUpdateCarModelDisplayImage = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateCarModelReference(id, { display_image: file });
      if (result) {
        setCarModelReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '表示画像を更新しました');
      }
    } catch (error) {
      console.error('カーモデルリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カーモデルリファレンスの名前更新
  const handleUpdateCarModelName = async (id: number, newName: string) => {
    if (!newName.trim()) {
      showToast('error', 'モデル名を入力してください');
      return;
    }
    setIsLoading(true);
    try {
      const result = await updateCarModelReference(id, { model_name: newName.trim() });
      if (result) {
        setCarModelReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'モデル名を更新しました');
      }
    } catch (error) {
      console.error('カーモデルリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カーモデルリファレンスのスタイル更新
  const handleUpdateCarModelStyle = async (id: number, newStyle: CarModelStyle) => {
    setIsLoading(true);
    try {
      const result = await updateCarModelReference(id, { style: newStyle });
      if (result) {
        setCarModelReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'スタイルを更新しました');
      }
    } catch (error) {
      console.error('カーモデルリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カーモデルリファレンスの削除
  const handleDeleteCarModelReference = async (id: number) => {
    if (!confirm('このカーモデルリファレンスを削除しますか？')) return;
    setIsLoading(true);
    try {
      const success = await deleteCarModelReference(id);
      if (success) {
        setCarModelReferences(prev => prev.filter(r => r.id !== id));
        showToast('success', 'カーモデルリファレンスを削除しました');
      }
    } catch (error) {
      console.error('カーモデルリファレンス削除エラー:', error);
      showToast('error', '削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カースポットリファレンスの追加
  const handleAddCarSpotReference = async () => {
    if (!newCarSpotName.trim() || !currentUser.id) {
      showToast('error', 'スポット名を入力してください');
      return;
    }
    if (carSpotReferences.length >= 5) {
      showToast('error', 'カースポットリファレンスは最大5件までです');
      return;
    }
    setIsLoading(true);
    try {
      const result = await createCarSpotReference(
        currentUser.id,
        newCarSpotName.trim(),
        newCarSpotCategory,
        newCarSpotPhoto || undefined,
        newCarSpotDisplayImage || undefined,
      );
      if (result) {
        setCarSpotReferences(prev => [...prev, result]);
        setNewCarSpotName('');
        setNewCarSpotCategory('other');
        setNewCarSpotPhoto(null);
        setNewCarSpotDisplayImage(null);
        showToast('success', 'カースポットリファレンスを追加しました');
      }
    } catch (error) {
      console.error('カースポットリファレンス追加エラー:', error);
      showToast('error', '追加に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カースポットリファレンスの参照画像更新
  const handleUpdateCarSpotPhoto = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateCarSpotReference(id, { photo: file });
      if (result) {
        setCarSpotReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '参照画像を更新しました');
      }
    } catch (error) {
      console.error('カースポットリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カースポットリファレンスの表示画像更新
  const handleUpdateCarSpotDisplayImage = async (id: number, file: File) => {
    setIsLoading(true);
    try {
      const result = await updateCarSpotReference(id, { display_image: file });
      if (result) {
        setCarSpotReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', '表示画像を更新しました');
      }
    } catch (error) {
      console.error('カースポットリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カースポットリファレンスの名前更新
  const handleUpdateCarSpotName = async (id: number, newName: string) => {
    if (!newName.trim()) {
      showToast('error', 'スポット名を入力してください');
      return;
    }
    setIsLoading(true);
    try {
      const result = await updateCarSpotReference(id, { spot_name: newName.trim() });
      if (result) {
        setCarSpotReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'スポット名を更新しました');
      }
    } catch (error) {
      console.error('カースポットリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カースポットリファレンスのカテゴリ更新
  const handleUpdateCarSpotCategory = async (id: number, newCategory: SpotCategory) => {
    setIsLoading(true);
    try {
      const result = await updateCarSpotReference(id, { category: newCategory });
      if (result) {
        setCarSpotReferences(prev => prev.map(r => r.id === id ? result : r));
        showToast('success', 'カテゴリを更新しました');
      }
    } catch (error) {
      console.error('カースポットリファレンス更新エラー:', error);
      showToast('error', '更新に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // カースポットリファレンスの削除
  const handleDeleteCarSpotReference = async (id: number) => {
    if (!confirm('このカースポットリファレンスを削除しますか？')) return;
    setIsLoading(true);
    try {
      const success = await deleteCarSpotReference(id);
      if (success) {
        setCarSpotReferences(prev => prev.filter(r => r.id !== id));
        showToast('success', 'カースポットリファレンスを削除しました');
      }
    } catch (error) {
      console.error('カースポットリファレンス削除エラー:', error);
      showToast('error', '削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 愛車選択変更
  const handleCarSelect = async (car: CarInfo) => {
    setSelectedCar(car);
    setIsCarSelectModalOpen(false);

    // 選択した愛車の設定を取得
    const carSettingsList = await fetchCarSettings(currentUser.id);
    if (carSettingsList && carSettingsList.length > 0) {
      const carSetting = carSettingsList.find(cs => cs.car_id === car.car_id);
      if (carSetting) {
        setCurrentCarSettings(carSetting);
        setSettings(prev => ({
          ...prev,
          numberManagement: {
            licensePlateText: carSetting.license_plate_text || '',
            logoMarkImageUrl: carSetting.logo_mark_image_url || undefined,
            originalNumberImageUrl: carSetting.original_number_image_url || undefined,
          },
          referenceRegistration: {
            favoriteCarName: carSetting.car_name || '',
            carPhotos: prev.referenceRegistration.carPhotos.map(cp => {
              if (cp.viewAngle === 'front') return { ...cp, imageUrl: carSetting.car_photo_front_url || undefined };
              if (cp.viewAngle === 'side') return { ...cp, imageUrl: carSetting.car_photo_side_url || undefined };
              if (cp.viewAngle === 'rear') return { ...cp, imageUrl: carSetting.car_photo_rear_url || undefined };
              if (cp.viewAngle === 'front_angled_7_3') return { ...cp, imageUrl: carSetting.car_photo_diagonal_url || undefined };
              if (cp.viewAngle === 'rear_angled_7_3') return { ...cp, imageUrl: carSetting.car_photo_rear_diagonal_url || undefined };
              return cp;
            }),
          },
        }));
      } else {
        setCurrentCarSettings(null);
        setSettings(getDefaultPersonalUserSettings());
      }
    }
  };

  // 愛車設定保存
  const handleSaveCarSettings = async () => {
    if (!selectedCar || !currentUser.id) {
      showToast('error', '愛車を選択してください');
      return;
    }

    setIsLoading(true);
    try {
      // DEBUG: uploadedFilesの中身を確認
      const fileKeys = Object.keys(uploadedFiles);
      console.log(`[DEBUG] handleSaveCarSettings - uploadedFiles keys: [${fileKeys.join(', ')}]`);
      console.log(`[DEBUG] handleSaveCarSettings - uploadedFiles details:`,
        Object.fromEntries(Object.entries(uploadedFiles).map(([k, v]) => [k, v ? `File(${v.name}, ${v.size}bytes)` : 'undefined']))
      );
      console.log(`[DEBUG] handleSaveCarSettings - deleteFlags:`, deleteFlags);

      const requestData: CarSettingsCreateUpdateRequest = {
        user_id: currentUser.id,
        car_id: selectedCar.car_id,
        license_plate_text: settings.numberManagement.licensePlateText,
        car_name: settings.referenceRegistration.favoriteCarName,
        ...uploadedFiles,
        ...deleteFlags,
      };

      // DEBUG: requestDataの中身を確認
      const requestFileKeys = ['car_photo_front', 'car_photo_side', 'car_photo_rear', 'car_photo_diagonal', 'car_photo_rear_diagonal', 'original_number_image', 'logo_mark_image']
        .filter(k => (requestData as any)[k]);
      console.log(`[DEBUG] handleSaveCarSettings - requestData file keys: [${requestFileKeys.join(', ')}]`);

      let result;
      if (currentCarSettings) {
        result = await updateCarSettings(currentCarSettings.id, requestData);
      } else {
        result = await createOrUpdateCarSettings(requestData);
      }

      if (result) {
        setCurrentCarSettings(result);
        setDeleteFlags({}); // 削除フラグをリセット
        setUploadedFiles({}); // アップロードファイルをリセット
        showToast('success', '愛車設定を保存しました');
      }
    } catch (error) {
      console.error('愛車設定保存エラー:', error);
      showToast('error', '保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCarName = (car: CarInfo): string => {
    if (car.car_nickname) {
      return `${car.car_brand_en} ${car.car_model_en} [${car.car_nickname}]`;
    }
    return `${car.car_brand_en} ${car.car_model_en}`;
  };

  // セグメントヘッダーコンポーネント
  const SegmentHeader: React.FC<{
    icon: React.ReactNode;
    title: string;
    isOpen: boolean;
    onToggle: () => void;
  }> = ({ icon, title, isOpen, onToggle }) => (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between text-xl font-medium text-indigo-300 border-b border-gray-600 pb-2 hover:text-indigo-200 transition-colors"
    >
      <span className="flex items-center">
        {icon}
        <span className="ml-2">{title}</span>
      </span>
      {isOpen ? (
        <ChevronDownIcon className="w-5 h-5" />
      ) : (
        <ArrowLeftIcon className="w-5 h-5 transform rotate-90" />
      )}
    </button>
  );

  // 現在選択中のセクション
  type SectionType = 'self' | 'car' | 'garage' | 'family' | 'other' | 'separator' | 'carTalent' | 'carModel' | 'carSpot';
  const [activeSection, setActiveSection] = useState<SectionType>('self');

  // メニュー項目（separatorはセパレーター用、adminOnlyは管理者専用）
  const allMenuItems: { id: SectionType; label: string; icon: React.ReactNode; isSeparator?: boolean; adminOnly?: boolean }[] = [
    { id: 'self', label: '自分', icon: <UserIcon className="w-5 h-5" /> },
    { id: 'car', label: '愛車', icon: <TruckIcon className="w-5 h-5" /> },
    { id: 'garage', label: 'ガレージ', icon: <HomeModernIcon className="w-5 h-5" /> },
    { id: 'family', label: 'ファミリー', icon: <UsersIcon className="w-5 h-5" /> },
    { id: 'other', label: 'お気に入り', icon: <StarIcon className="w-5 h-5" /> },
    { id: 'separator', label: '', icon: null, isSeparator: true, adminOnly: true },
    { id: 'carTalent', label: 'カータレント', icon: <SparklesSolidIcon className="w-5 h-5" />, adminOnly: true },
    { id: 'carModel', label: 'カーモデル', icon: <UserGroupIcon className="w-5 h-5" />, adminOnly: true },
    { id: 'carSpot', label: 'カースポット', icon: <HomeModernIcon className="w-5 h-5" />, adminOnly: true },
  ];

  // 管理者でない場合はadminOnly項目を除外
  const menuItems = currentUser.isAdmin
    ? allMenuItems
    : allMenuItems.filter(item => !item.adminOnly);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center z-50 p-4 pt-8 pb-20 overflow-y-auto">
      <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl flex flex-col overflow-hidden">
        {/* ヘッダー */}
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-indigo-400">参照設定</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-700"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* メインコンテンツ（左右分割） */}
        <div className="flex flex-1 overflow-hidden">
          {/* 左サイドメニュー */}
          <nav className="w-48 flex-shrink-0 bg-gray-900/50 border-r border-gray-700 p-3 space-y-1">
            {menuItems.map((item) => (
              item.isSeparator ? (
                <div key={item.id} className="my-2 border-t border-gray-600" />
              ) : (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                    activeSection === item.id
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  {item.icon}
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              )
            ))}
          </nav>

          {/* 右コンテンツエリア */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
            {/* 自分リファレンス */}
            {activeSection === 'self' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-indigo-300 border-b border-gray-600 pb-2">
                  自分リファレンス
                </h3>
                <p className="text-sm text-gray-400">
                  自分の写真を登録しておくと、画像生成時に参照として利用できます。
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(selfPhotoTypeLabels).map(([type, label]) => {
                    const ref = selfReferences.find(r => r.photo_type === type);
                    return (
                      <div key={type} className="space-y-2">
                        <ImageUpload
                          label={label}
                          initialPreviewUrl={ref?.photo_url || undefined}
                          showDeleteButton={!!ref}
                          onImageSelect={(file) => handleSelfPhotoUpload(type as SelfPhotoType, file)}
                          onDelete={() => ref && handleDeleteSelfReference(ref.id)}
                        />
                        {ref && (
                          <p className="text-xs text-gray-500 text-center">
                            {ref.reference_key}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 愛車リファレンス */}
            {activeSection === 'car' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-indigo-300 border-b border-gray-600 pb-2">
                  愛車リファレンス
                </h3>
                {/* 愛車選択 */}
                <div className="bg-gray-700/50 rounded-lg p-3">
                  <div className="flex items-center space-x-3">
                    {selectedCar?.car_image_url && (
                      <img
                        src={selectedCar.car_image_url}
                        alt={formatCarName(selectedCar)}
                        className="w-20 h-14 object-cover rounded border border-gray-600"
                      />
                    )}
                    <div className="flex-grow min-w-0">
                      <div className="text-sm text-white truncate">
                        {selectedCar ? formatCarName(selectedCar) : '車両を選択してください'}
                      </div>
                    </div>
                    {carList.length >= 1 && (
                      <button
                        type="button"
                        onClick={() => setIsCarSelectModalOpen(true)}
                        className="flex-shrink-0 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-md transition-colors"
                      >
                        変更
                      </button>
                    )}
                  </div>
                </div>

                {/* ナンバー管理 */}
                <div className="space-y-3">
                  <h4 className="text-base font-medium text-gray-300">ナンバー管理</h4>
                  <div className="space-y-3 bg-gray-800/30 p-4 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        文字列 (例: 横浜 330 な 2960)
                      </label>
                      <input
                        type="text"
                        value={settings.numberManagement.licensePlateText || ''}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          numberManagement: {
                            ...prev.numberManagement,
                            licensePlateText: e.target.value,
                          },
                        }))}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                        placeholder="横浜 330 な 2960"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <ImageUpload
                          ref={originalNumberImageUploadRef}
                          label="オリジナルナンバー画像"
                          initialPreviewUrl={currentCarSettings?.original_number_image_url || undefined}
                          showDeleteButton={!!currentCarSettings?.original_number_image_url}
                          onImageSelect={(file) => file && setUploadedFiles(prev => ({ ...prev, original_number_image: file }))}
                          onDelete={() => setDeleteFlags(prev => ({ ...prev, delete_original_number_image: true }))}
                        />
                        {deleteFlags.delete_original_number_image && (
                          <p className="text-xs text-red-400 mt-1">※保存で削除されます</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* リファレンス登録 */}
                <div className="space-y-3">
                  <h4 className="text-base font-medium text-gray-300">リファレンス登録</h4>
                  <div className="space-y-3 bg-gray-800/30 p-4 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        愛車名前
                      </label>
                      <input
                        type="text"
                        value={settings.referenceRegistration.favoriteCarName || ''}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          referenceRegistration: {
                            ...prev.referenceRegistration,
                            favoriteCarName: e.target.value,
                          },
                        }))}
                        className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                        placeholder="愛車の名前"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <ImageUpload
                          label="フロント写真"
                          initialPreviewUrl={currentCarSettings?.car_photo_front_url || undefined}
                          showDeleteButton={!!currentCarSettings?.car_photo_front_url}
                          onImageSelect={(file) => { if (file) { console.log(`[DEBUG] onImageSelect: car_photo_front = ${file.name}`); setUploadedFiles(prev => { const next = { ...prev, car_photo_front: file }; console.log(`[DEBUG] setUploadedFiles updater: keys=[${Object.keys(next).join(', ')}]`); return next; }); }}}
                          onDelete={() => setDeleteFlags(prev => ({ ...prev, delete_car_photo_front: true }))}
                        />
                        {deleteFlags.delete_car_photo_front && (
                          <p className="text-xs text-red-400 mt-1">※保存で削除されます</p>
                        )}
                      </div>
                      <div>
                        <ImageUpload
                          label="サイド写真"
                          initialPreviewUrl={currentCarSettings?.car_photo_side_url || undefined}
                          showDeleteButton={!!currentCarSettings?.car_photo_side_url}
                          onImageSelect={(file) => { if (file) { console.log(`[DEBUG] onImageSelect: car_photo_side = ${file.name}`); setUploadedFiles(prev => { const next = { ...prev, car_photo_side: file }; console.log(`[DEBUG] setUploadedFiles updater: keys=[${Object.keys(next).join(', ')}]`); return next; }); }}}
                          onDelete={() => setDeleteFlags(prev => ({ ...prev, delete_car_photo_side: true }))}
                        />
                        {deleteFlags.delete_car_photo_side && (
                          <p className="text-xs text-red-400 mt-1">※保存で削除されます</p>
                        )}
                      </div>
                      <div>
                        <ImageUpload
                          label="リア写真"
                          initialPreviewUrl={currentCarSettings?.car_photo_rear_url || undefined}
                          showDeleteButton={!!currentCarSettings?.car_photo_rear_url}
                          onImageSelect={(file) => { if (file) { console.log(`[DEBUG] onImageSelect: car_photo_rear = ${file.name}`); setUploadedFiles(prev => { const next = { ...prev, car_photo_rear: file }; console.log(`[DEBUG] setUploadedFiles updater: keys=[${Object.keys(next).join(', ')}]`); return next; }); }}}
                          onDelete={() => setDeleteFlags(prev => ({ ...prev, delete_car_photo_rear: true }))}
                        />
                        {deleteFlags.delete_car_photo_rear && (
                          <p className="text-xs text-red-400 mt-1">※保存で削除されます</p>
                        )}
                      </div>
                      <div>
                        <ImageUpload
                          label="斜め前右写真"
                          initialPreviewUrl={currentCarSettings?.car_photo_diagonal_url || undefined}
                          showDeleteButton={!!currentCarSettings?.car_photo_diagonal_url}
                          onImageSelect={(file) => { if (file) { console.log(`[DEBUG] onImageSelect: car_photo_diagonal = ${file.name}`); setUploadedFiles(prev => { const next = { ...prev, car_photo_diagonal: file }; console.log(`[DEBUG] setUploadedFiles updater: keys=[${Object.keys(next).join(', ')}]`); return next; }); }}}
                          onDelete={() => setDeleteFlags(prev => ({ ...prev, delete_car_photo_diagonal: true }))}
                        />
                        {deleteFlags.delete_car_photo_diagonal && (
                          <p className="text-xs text-red-400 mt-1">※保存で削除されます</p>
                        )}
                      </div>
                      <div>
                        <ImageUpload
                          label="斜め後ろ7:3写真"
                          initialPreviewUrl={currentCarSettings?.car_photo_rear_diagonal_url || undefined}
                          showDeleteButton={!!currentCarSettings?.car_photo_rear_diagonal_url}
                          onImageSelect={(file) => { if (file) { console.log(`[DEBUG] onImageSelect: car_photo_rear_diagonal = ${file.name}`); setUploadedFiles(prev => { const next = { ...prev, car_photo_rear_diagonal: file }; console.log(`[DEBUG] setUploadedFiles updater: keys=[${Object.keys(next).join(', ')}]`); return next; }); }}}
                          onDelete={() => setDeleteFlags(prev => ({ ...prev, delete_car_photo_rear_diagonal: true }))}
                        />
                        {deleteFlags.delete_car_photo_rear_diagonal && (
                          <p className="text-xs text-red-400 mt-1">※保存で削除されます</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 愛車設定保存ボタン */}
                <button
                  type="button"
                  onClick={handleSaveCarSettings}
                  disabled={isLoading || !selectedCar}
                  className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                >
                  <SaveIcon className="w-5 h-5 mr-2" />
                  愛車設定を保存
                </button>
              </div>
            )}

            {/* ガレージリファレンス */}
            {activeSection === 'garage' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-indigo-300 border-b border-gray-600 pb-2">
                  ガレージリファレンス
                </h3>
                <p className="text-sm text-gray-400">
                  ガレージや駐車場の写真を登録できます。（最大5件）
                </p>

                {/* 既存のガレージリファレンス一覧 */}
                {garageReferences.map((ref) => (
                  <div key={ref.id} className="flex items-center space-x-3 bg-gray-700/50 p-3 rounded-lg">
                    <div
                      className="flex-shrink-0 w-20 h-20 relative border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                        const file = e.dataTransfer.files[0];
                        if (file && file.type.startsWith('image/')) {
                          handleUpdateGaragePhoto(ref.id, file);
                        }
                      }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) handleUpdateGaragePhoto(ref.id, file);
                        };
                        input.click();
                      }}
                    >
                      {ref.photo_url ? (
                        <img
                          src={ref.photo_url}
                          alt={ref.garage_name}
                          className="w-full h-full object-cover rounded-md"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                          <HomeModernIcon className="w-8 h-8" />
                          <span className="text-[10px] mt-1">ドロップ</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-grow">
                      <input
                        type="text"
                        defaultValue={ref.garage_name}
                        onBlur={(e) => {
                          if (e.target.value !== ref.garage_name) {
                            handleUpdateGarageName(ref.id, e.target.value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-white font-medium text-sm focus:border-indigo-500 focus:outline-none"
                        placeholder="ガレージ名"
                      />
                      <p className="text-xs text-gray-500 mt-1">{ref.reference_key}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteGarageReference(ref.id)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-md transition-colors"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {/* 新規追加フォーム */}
                {garageReferences.length < 5 && (
                  <div className="border-t border-gray-600 pt-4 space-y-3">
                    <h4 className="text-sm font-medium text-gray-300">新規追加</h4>
                    <div className="flex items-start space-x-3">
                      {/* 画像ドロップエリア */}
                      <div
                        className="flex-shrink-0 w-20 h-20 border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith('image/')) {
                            setNewGaragePhoto(file);
                          }
                        }}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) setNewGaragePhoto(file);
                          };
                          input.click();
                        }}
                      >
                        {newGaragePhoto ? (
                          <img
                            src={URL.createObjectURL(newGaragePhoto)}
                            alt="プレビュー"
                            className="w-full h-full object-cover rounded-md"
                          />
                        ) : (
                          <>
                            <PlusIcon className="w-6 h-6 text-gray-500" />
                            <span className="text-[10px] text-gray-500 mt-1">画像</span>
                          </>
                        )}
                      </div>
                      <div className="flex-grow space-y-2">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            ガレージ名
                          </label>
                          <input
                            type="text"
                            value={newGarageName}
                            onChange={(e) => setNewGarageName(e.target.value)}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                            placeholder="例: 自宅ガレージ、月極駐車場"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddGarageReference}
                          disabled={isLoading || !newGarageName.trim()}
                          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                        >
                          <PlusIcon className="w-5 h-5 mr-1" />
                          追加
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ファミリーリファレンス */}
            {activeSection === 'family' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-indigo-300 border-b border-gray-600 pb-2">
                  ファミリーリファレンス
                </h3>
                <p className="text-sm text-gray-400">
                  家族やペットなどの写真を登録できます。（最大5件）
                </p>

                {/* 既存のファミリーリファレンス一覧 */}
                {familyReferences.map((ref) => (
                  <div key={ref.id} className="flex items-center space-x-3 bg-gray-700/50 p-3 rounded-lg">
                    <div
                      className="flex-shrink-0 w-20 h-20 relative border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                        const file = e.dataTransfer.files[0];
                        if (file && file.type.startsWith('image/')) {
                          handleUpdateFamilyPhoto(ref.id, file);
                        }
                      }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) handleUpdateFamilyPhoto(ref.id, file);
                        };
                        input.click();
                      }}
                    >
                      {ref.photo_url ? (
                        <img
                          src={ref.photo_url}
                          alt={ref.family_name}
                          className="w-full h-full object-cover rounded-md"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                          <UserIcon className="w-8 h-8" />
                          <span className="text-[10px] mt-1">ドロップ</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-grow">
                      <input
                        type="text"
                        defaultValue={ref.family_name}
                        onBlur={(e) => {
                          if (e.target.value !== ref.family_name) {
                            handleUpdateFamilyName(ref.id, e.target.value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-white font-medium text-sm focus:border-indigo-500 focus:outline-none"
                        placeholder="ファミリー名"
                      />
                      <p className="text-xs text-gray-500 mt-1">{ref.reference_key}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteFamilyReference(ref.id)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-md transition-colors"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {/* 新規追加フォーム */}
                {familyReferences.length < 5 && (
                  <div className="border-t border-gray-600 pt-4 space-y-3">
                    <h4 className="text-sm font-medium text-gray-300">新規追加</h4>
                    <div className="flex items-start space-x-3">
                      {/* 画像ドロップエリア */}
                      <div
                        className="flex-shrink-0 w-20 h-20 border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith('image/')) {
                            setNewFamilyPhoto(file);
                          }
                        }}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) setNewFamilyPhoto(file);
                          };
                          input.click();
                        }}
                      >
                        {newFamilyPhoto ? (
                          <img
                            src={URL.createObjectURL(newFamilyPhoto)}
                            alt="プレビュー"
                            className="w-full h-full object-cover rounded-md"
                          />
                        ) : (
                          <>
                            <PlusIcon className="w-6 h-6 text-gray-500" />
                            <span className="text-[10px] text-gray-500 mt-1">画像</span>
                          </>
                        )}
                      </div>
                      <div className="flex-grow space-y-2">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            ファミリー名
                          </label>
                          <input
                            type="text"
                            value={newFamilyName}
                            onChange={(e) => setNewFamilyName(e.target.value)}
                            className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200"
                            placeholder="例: 妻、息子、愛犬"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddFamilyReference}
                          disabled={isLoading || !newFamilyName.trim()}
                          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                        >
                          <PlusIcon className="w-5 h-5 mr-1" />
                          追加
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* カータレントリファレンス（旧スタッフ/モデル） */}
            {activeSection === 'carTalent' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-indigo-300 border-b border-gray-600 pb-2">
                  カータレントリファレンス
                </h3>
                <p className="text-sm text-gray-400">
                  カータレントの写真を登録できます。スタイル（リアル、アニメ、イラスト、3D）を選択してください。（最大5件）
                </p>
                <p className="text-xs text-gray-500">
                  ※参照画像: API用（非公開） / 表示画像: ユーザーに表示する画像
                </p>

                {/* 既存のスタッフリファレンス一覧 */}
                {staffReferences.map((ref) => (
                  <div key={ref.id} className="bg-gray-700/50 p-3 rounded-lg space-y-3">
                    <div className="flex items-start space-x-3">
                      {/* 参照画像（API用） */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">参照画像</p>
                        <div
                          className="w-16 h-16 relative border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              handleUpdateStaffPhoto(ref.id, file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleUpdateStaffPhoto(ref.id, file);
                            };
                            input.click();
                          }}
                        >
                          {ref.photo_url ? (
                            <img
                              src={ref.photo_url}
                              alt={`${ref.staff_name} 参照`}
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                              <UserGroupIcon className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                      </div>
                      {/* 表示画像（ユーザー表示用） */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">表示画像</p>
                        <div
                          className="w-16 h-16 relative border-2 border-dashed border-green-600 rounded-lg hover:border-green-400 transition-colors cursor-pointer"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-green-400', 'bg-green-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              handleUpdateStaffDisplayImage(ref.id, file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleUpdateStaffDisplayImage(ref.id, file);
                            };
                            input.click();
                          }}
                        >
                          {ref.display_image_url ? (
                            <img
                              src={ref.display_image_url}
                              alt={`${ref.staff_name} 表示`}
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-green-500">
                              <SparklesSolidIcon className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-grow space-y-2">
                        <input
                          type="text"
                          defaultValue={ref.staff_name}
                          onBlur={(e) => {
                            if (e.target.value !== ref.staff_name) {
                              handleUpdateStaffName(ref.id, e.target.value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-white font-medium text-sm focus:border-indigo-500 focus:outline-none"
                          placeholder="タレント名"
                        />
                        <select
                          value={ref.style}
                          onChange={(e) => handleUpdateStaffStyle(ref.id, e.target.value as StaffStyle)}
                          className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                        >
                          {Object.entries(staffStyleLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500">{ref.reference_key}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteStaffReference(ref.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-md transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* 新規追加フォーム */}
                {staffReferences.length < 5 && (
                  <div className="border-t border-gray-600 pt-4 space-y-3">
                    <h4 className="text-sm font-medium text-gray-300">新規追加</h4>
                    <div className="flex items-start space-x-3">
                      {/* 参照画像ドロップエリア */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">参照画像</p>
                        <div
                          className="w-16 h-16 border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              setNewStaffPhoto(file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) setNewStaffPhoto(file);
                            };
                            input.click();
                          }}
                        >
                          {newStaffPhoto ? (
                            <img
                              src={URL.createObjectURL(newStaffPhoto)}
                              alt="参照プレビュー"
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <PlusIcon className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                      </div>
                      {/* 表示画像ドロップエリア */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">表示画像</p>
                        <div
                          className="w-16 h-16 border-2 border-dashed border-green-600 rounded-lg hover:border-green-400 transition-colors cursor-pointer flex flex-col items-center justify-center"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-green-400', 'bg-green-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              setNewStaffDisplayImage(file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) setNewStaffDisplayImage(file);
                            };
                            input.click();
                          }}
                        >
                          {newStaffDisplayImage ? (
                            <img
                              src={URL.createObjectURL(newStaffDisplayImage)}
                              alt="表示プレビュー"
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <PlusIcon className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      </div>
                      <div className="flex-grow space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              タレント名
                            </label>
                            <input
                              type="text"
                              value={newStaffName}
                              onChange={(e) => setNewStaffName(e.target.value)}
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                              placeholder="例: 山田太郎"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              スタイル
                            </label>
                            <select
                              value={newStaffStyle}
                              onChange={(e) => setNewStaffStyle(e.target.value as StaffStyle)}
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                            >
                              {Object.entries(staffStyleLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleAddStaffReference}
                          disabled={isLoading || !newStaffName.trim()}
                          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                        >
                          <PlusIcon className="w-5 h-5 mr-1" />
                          追加
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* カーモデルリファレンス */}
            {activeSection === 'carModel' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-indigo-300 border-b border-gray-600 pb-2">
                  カーモデルリファレンス
                </h3>
                <p className="text-sm text-gray-400">
                  カーモデルの写真を登録できます。スタイル（リアル、アニメ、イラスト、3D）を選択してください。（最大5件）
                </p>
                <p className="text-xs text-gray-500">
                  ※参照画像: API用（非公開） / 表示画像: ユーザーに表示する画像
                </p>

                {/* 既存のカーモデルリファレンス一覧 */}
                {carModelReferences.map((ref) => (
                  <div key={ref.id} className="bg-gray-700/50 p-3 rounded-lg space-y-3">
                    <div className="flex items-start space-x-3">
                      {/* 参照画像（API用） */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">参照画像</p>
                        <div
                          className="w-16 h-16 relative border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              handleUpdateCarModelPhoto(ref.id, file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleUpdateCarModelPhoto(ref.id, file);
                            };
                            input.click();
                          }}
                        >
                          {ref.photo_url ? (
                            <img
                              src={ref.photo_url}
                              alt={`${ref.model_name} 参照`}
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                              <UserGroupIcon className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                      </div>
                      {/* 表示画像（ユーザー表示用） */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">表示画像</p>
                        <div
                          className="w-16 h-16 relative border-2 border-dashed border-green-600 rounded-lg hover:border-green-400 transition-colors cursor-pointer"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-green-400', 'bg-green-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              handleUpdateCarModelDisplayImage(ref.id, file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleUpdateCarModelDisplayImage(ref.id, file);
                            };
                            input.click();
                          }}
                        >
                          {ref.display_image_url ? (
                            <img
                              src={ref.display_image_url}
                              alt={`${ref.model_name} 表示`}
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-green-500">
                              <SparklesSolidIcon className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-grow space-y-2">
                        <input
                          type="text"
                          defaultValue={ref.model_name}
                          onBlur={(e) => {
                            if (e.target.value !== ref.model_name) {
                              handleUpdateCarModelName(ref.id, e.target.value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-white font-medium text-sm focus:border-indigo-500 focus:outline-none"
                          placeholder="モデル名"
                        />
                        <select
                          value={ref.style}
                          onChange={(e) => handleUpdateCarModelStyle(ref.id, e.target.value as CarModelStyle)}
                          className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                        >
                          {Object.entries(carModelStyleLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500">{ref.reference_key}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteCarModelReference(ref.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-md transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* 新規追加フォーム */}
                {carModelReferences.length < 5 && (
                  <div className="border-t border-gray-600 pt-4 space-y-3">
                    <h4 className="text-sm font-medium text-gray-300">新規追加</h4>
                    <div className="flex items-start space-x-3">
                      {/* 参照画像ドロップエリア */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">参照画像</p>
                        <div
                          className="w-16 h-16 border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              setNewCarModelPhoto(file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) setNewCarModelPhoto(file);
                            };
                            input.click();
                          }}
                        >
                          {newCarModelPhoto ? (
                            <img
                              src={URL.createObjectURL(newCarModelPhoto)}
                              alt="参照プレビュー"
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <PlusIcon className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                      </div>
                      {/* 表示画像ドロップエリア */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">表示画像</p>
                        <div
                          className="w-16 h-16 border-2 border-dashed border-green-600 rounded-lg hover:border-green-400 transition-colors cursor-pointer flex flex-col items-center justify-center"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-green-400', 'bg-green-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              setNewCarModelDisplayImage(file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) setNewCarModelDisplayImage(file);
                            };
                            input.click();
                          }}
                        >
                          {newCarModelDisplayImage ? (
                            <img
                              src={URL.createObjectURL(newCarModelDisplayImage)}
                              alt="表示プレビュー"
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <PlusIcon className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      </div>
                      <div className="flex-grow space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              モデル名
                            </label>
                            <input
                              type="text"
                              value={newCarModelName}
                              onChange={(e) => setNewCarModelName(e.target.value)}
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                              placeholder="例: モデルA"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              スタイル
                            </label>
                            <select
                              value={newCarModelStyle}
                              onChange={(e) => setNewCarModelStyle(e.target.value as CarModelStyle)}
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                            >
                              {Object.entries(carModelStyleLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleAddCarModelReference}
                          disabled={isLoading || !newCarModelName.trim()}
                          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                        >
                          <PlusIcon className="w-5 h-5 mr-1" />
                          追加
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* カースポットリファレンス */}
            {activeSection === 'carSpot' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-indigo-300 border-b border-gray-600 pb-2">
                  カースポットリファレンス
                </h3>
                <p className="text-sm text-gray-400">
                  カースポット（撮影スポット）の写真を登録できます。カテゴリを選択してください。（最大5件）
                </p>
                <p className="text-xs text-gray-500">
                  ※参照画像: API用（非公開） / 表示画像: ユーザーに表示する画像
                </p>

                {/* 既存のカースポットリファレンス一覧 */}
                {carSpotReferences.map((ref) => (
                  <div key={ref.id} className="bg-gray-700/50 p-3 rounded-lg space-y-3">
                    <div className="flex items-start space-x-3">
                      {/* 参照画像（API用） */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">参照画像</p>
                        <div
                          className="w-16 h-16 relative border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              handleUpdateCarSpotPhoto(ref.id, file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleUpdateCarSpotPhoto(ref.id, file);
                            };
                            input.click();
                          }}
                        >
                          {ref.photo_url ? (
                            <img
                              src={ref.photo_url}
                              alt={`${ref.spot_name} 参照`}
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                              <HomeModernIcon className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                      </div>
                      {/* 表示画像（ユーザー表示用） */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">表示画像</p>
                        <div
                          className="w-16 h-16 relative border-2 border-dashed border-green-600 rounded-lg hover:border-green-400 transition-colors cursor-pointer"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-green-400', 'bg-green-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              handleUpdateCarSpotDisplayImage(ref.id, file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleUpdateCarSpotDisplayImage(ref.id, file);
                            };
                            input.click();
                          }}
                        >
                          {ref.display_image_url ? (
                            <img
                              src={ref.display_image_url}
                              alt={`${ref.spot_name} 表示`}
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-green-500">
                              <SparklesSolidIcon className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-grow space-y-2">
                        <input
                          type="text"
                          defaultValue={ref.spot_name}
                          onBlur={(e) => {
                            if (e.target.value !== ref.spot_name) {
                              handleUpdateCarSpotName(ref.id, e.target.value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-white font-medium text-sm focus:border-indigo-500 focus:outline-none"
                          placeholder="スポット名"
                        />
                        <select
                          value={ref.category}
                          onChange={(e) => handleUpdateCarSpotCategory(ref.id, e.target.value as SpotCategory)}
                          className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                        >
                          {Object.entries(spotCategoryLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500">{ref.reference_key}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteCarSpotReference(ref.id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-md transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* 新規追加フォーム */}
                {carSpotReferences.length < 5 && (
                  <div className="border-t border-gray-600 pt-4 space-y-3">
                    <h4 className="text-sm font-medium text-gray-300">新規追加</h4>
                    <div className="flex items-start space-x-3">
                      {/* 参照画像ドロップエリア */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">参照画像</p>
                        <div
                          className="w-16 h-16 border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              setNewCarSpotPhoto(file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) setNewCarSpotPhoto(file);
                            };
                            input.click();
                          }}
                        >
                          {newCarSpotPhoto ? (
                            <img
                              src={URL.createObjectURL(newCarSpotPhoto)}
                              alt="参照プレビュー"
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <PlusIcon className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                      </div>
                      {/* 表示画像ドロップエリア */}
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 mb-1 text-center">表示画像</p>
                        <div
                          className="w-16 h-16 border-2 border-dashed border-green-600 rounded-lg hover:border-green-400 transition-colors cursor-pointer flex flex-col items-center justify-center"
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('border-green-400', 'bg-green-500/10');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-green-400', 'bg-green-500/10');
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/')) {
                              setNewCarSpotDisplayImage(file);
                            }
                          }}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) setNewCarSpotDisplayImage(file);
                            };
                            input.click();
                          }}
                        >
                          {newCarSpotDisplayImage ? (
                            <img
                              src={URL.createObjectURL(newCarSpotDisplayImage)}
                              alt="表示プレビュー"
                              className="w-full h-full object-cover rounded-md"
                            />
                          ) : (
                            <PlusIcon className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      </div>
                      <div className="flex-grow space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              スポット名
                            </label>
                            <input
                              type="text"
                              value={newCarSpotName}
                              onChange={(e) => setNewCarSpotName(e.target.value)}
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                              placeholder="例: 富士山麓"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              カテゴリ
                            </label>
                            <select
                              value={newCarSpotCategory}
                              onChange={(e) => setNewCarSpotCategory(e.target.value as SpotCategory)}
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                            >
                              {Object.entries(spotCategoryLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleAddCarSpotReference}
                          disabled={isLoading || !newCarSpotName.trim()}
                          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                        >
                          <PlusIcon className="w-5 h-5 mr-1" />
                          追加
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* お気に入りリファレンス */}
            {activeSection === 'other' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-indigo-300 border-b border-gray-600 pb-2">
                  お気に入りリファレンス
                </h3>
                <p className="text-sm text-gray-400">
                  その他の参照画像を登録できます。（最大5件）
                </p>

                {/* 既存のその他リファレンス一覧 */}
                {otherReferences.map((ref) => (
                  <div key={ref.id} className="flex items-center space-x-3 bg-gray-700/50 p-3 rounded-lg">
                    <div
                      className="flex-shrink-0 w-20 h-20 relative border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer"
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                      }}
                      onDragLeave={(e) => {
                        e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                        const file = e.dataTransfer.files[0];
                        if (file && file.type.startsWith('image/')) {
                          handleUpdateOtherPhoto(ref.id, file);
                        }
                      }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) handleUpdateOtherPhoto(ref.id, file);
                        };
                        input.click();
                      }}
                    >
                      {ref.photo_url ? (
                        <img
                          src={ref.photo_url}
                          alt={ref.reference_name}
                          className="w-full h-full object-cover rounded-md"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                          <StarIcon className="w-8 h-8" />
                          <span className="text-[10px] mt-1">ドロップ</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-grow">
                      <input
                        type="text"
                        defaultValue={ref.reference_name}
                        onBlur={(e) => {
                          if (e.target.value !== ref.reference_name) {
                            handleUpdateOtherName(ref.id, e.target.value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          }
                        }}
                        className="w-full p-1.5 bg-gray-700 border border-gray-600 rounded-md text-white font-medium text-sm focus:border-indigo-500 focus:outline-none"
                        placeholder="リファレンス名"
                      />
                      <p className="text-xs text-gray-400 mt-1">{ref.category_display}</p>
                      <p className="text-xs text-gray-500">{ref.reference_key}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteOtherReference(ref.id)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-md transition-colors"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {/* 新規追加フォーム */}
                {otherReferences.length < 5 && (
                  <div className="border-t border-gray-600 pt-4 space-y-3">
                    <h4 className="text-sm font-medium text-gray-300">新規追加</h4>
                    <div className="flex items-start space-x-3">
                      {/* 画像ドロップエリア */}
                      <div
                        className="flex-shrink-0 w-20 h-20 border-2 border-dashed border-gray-600 rounded-lg hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-500/10');
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-500/10');
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith('image/')) {
                            setNewOtherPhoto(file);
                          }
                        }}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) setNewOtherPhoto(file);
                          };
                          input.click();
                        }}
                      >
                        {newOtherPhoto ? (
                          <img
                            src={URL.createObjectURL(newOtherPhoto)}
                            alt="プレビュー"
                            className="w-full h-full object-cover rounded-md"
                          />
                        ) : (
                          <>
                            <PlusIcon className="w-6 h-6 text-gray-500" />
                            <span className="text-[10px] text-gray-500 mt-1">画像</span>
                          </>
                        )}
                      </div>
                      <div className="flex-grow space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              リファレンス名
                            </label>
                            <input
                              type="text"
                              value={newOtherName}
                              onChange={(e) => setNewOtherName(e.target.value)}
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                              placeholder="例: お気に入りの景色"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              カテゴリ
                            </label>
                            <select
                              value={newOtherCategory}
                              onChange={(e) => setNewOtherCategory(e.target.value as ReferenceCategory)}
                              className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-gray-200 text-sm"
                            >
                              {Object.entries(referenceCategoryLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleAddOtherReference}
                          disabled={isLoading || !newOtherName.trim()}
                          className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                        >
                          <PlusIcon className="w-5 h-5 mr-1" />
                          追加
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ローディングオーバーレイ */}
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
            <div className="text-white">処理中...</div>
          </div>
        )}
      </div>

      {/* 愛車選択モーダル */}
      {isCarSelectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">愛車を選択</h3>
              <button
                onClick={() => setIsCarSelectModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {carList.length === 0 ? (
                <p className="text-gray-400 text-center py-8">愛車が登録されていません</p>
              ) : (
                <div className="space-y-3">
                  {carList.map((car) => (
                    <button
                      key={car.id}
                      onClick={() => handleCarSelect(car)}
                      className={`w-full p-3 rounded-lg border transition-all duration-200 flex items-center space-x-3 ${
                        selectedCar?.id === car.id
                          ? 'border-indigo-500 bg-indigo-600/20'
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                        <img
                          src={car.car_image_url}
                          alt={formatCarName(car)}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium text-white">
                          {formatCarName(car)}
                        </div>
                        <div className="text-xs text-gray-400">
                          {car.car_brand_ja} {car.car_model_ja}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferenceSettingsView;
